import { observable, makeObservable, actionBound } from "mobx";
import EwaybillAccountShippingAddressDTO from "@/dto/ewaybillAccount/ShippingAddressDTO";
import {
    batchUpdateEwaybillAccountQuantity,
    loadEwaybillAccountsList,
    syncEwaybillAccount,
    UpdateEwaybillAccountQuantityPostData,
} from "@/services/api/setting/ewaybillAccount";
import EwaybillAccountDTO from "@/dto/ewaybillAccount/EwaybillAccountDTO";
import BranchAccountDTO from "@/dto/ewaybillAccount/BranchAccountDTO";
import SubscriptionDTO from "@/dto/ewaybillAccount/SubscriptionDTO";

class EwaybillAccountStore {
    constructor() {
        makeObservable(this, {
            loading: observable,
            rawEwaybillAccounts: observable,
            ewaybillShippingAddressMapping: observable,
            ewaybillAccountMapping: observable,
            branchAccountMapping: observable,
            subscriptionMapping: observable,
            branchAccountIdToBranchAccountMap: observable,
            ewaybillAccountId2AccountMapping: observable,
            ensure: actionBound,
            batchUpdateEwaybillAccountQuantity: actionBound,
            syncEwaybillAccountByAddressIdAndRefreshAccountList: actionBound,
            loadEwaybillAccountData: actionBound,
            clear: actionBound,
        });
    }

    loading = false;

    rawEwaybillAccounts: EwaybillAccountDTO[] = [];

    ewaybillShippingAddressMapping = new Map<number, EwaybillAccountShippingAddressDTO>();

    /**
     * 电子面单地址id -> EwaybillAccount
     */
    ewaybillAccountMapping = new Map<number, EwaybillAccountDTO>();

    /**
     * 电子面单地址id -> BranchAccount
     */
    branchAccountMapping = new Map<number, BranchAccountDTO>();

    subscriptionMapping: Map<number, SubscriptionDTO> = new Map<number, SubscriptionDTO>();

    branchAccountIdToBranchAccountMap: Map<number, BranchAccountDTO> = new Map<number, BranchAccountDTO>();

    ewaybillAccountId2AccountMapping = new Map<number, EwaybillAccountDTO>();

    async ensure() {
        if (!this.rawEwaybillAccounts.length) {
            await this.loadEwaybillAccountData();
        }
    }

    /**
     * 本地更新，使用的面单数量
     */
    async batchUpdateEwaybillAccountQuantity(
        data: Pick<UpdateEwaybillAccountQuantityPostData, "allocatedQuantity" | "ewaybillShippingAddressId">[],
    ) {
        try {
            await this.ensure();

            const updateEwaybillAccountQuantityPostData: UpdateEwaybillAccountQuantityPostData[] = [];

            data.forEach((it) => {
                const { ewaybillShippingAddressId, allocatedQuantity } = it;

                if (!allocatedQuantity) {
                    return;
                }

                if (!ewaybillShippingAddressId) {
                    return;
                }

                const branchAccount = this.branchAccountMapping.get(ewaybillShippingAddressId);
                const subscription = this.subscriptionMapping.get(ewaybillShippingAddressId);

                if (branchAccount && subscription) {
                    updateEwaybillAccountQuantityPostData.push({
                        ewaybillShippingAddressId,
                        allocatedQuantity,
                        ewaybillBranchAccountId: branchAccount.id,
                        ewaybillSubscriptionId: subscription.id,
                    });

                    if (typeof allocatedQuantity === "number" && typeof branchAccount?.quantity === "number") {
                        branchAccount.quantity -= allocatedQuantity;
                    }
                }
            });

            if (!updateEwaybillAccountQuantityPostData.length) {
                return;
            }

            const results = await batchUpdateEwaybillAccountQuantity(updateEwaybillAccountQuantityPostData);

            for (let i = 0; i < results.length; i++) {
                const result = results[i];

                const branchAccount = this.branchAccountMapping.get(result.ewaybillShippingAddressId);

                if (branchAccount && typeof result?.quantity === "number") {
                    branchAccount.quantity = result.quantity;
                }
            }
        } catch (error) {
            console.error("更新电子面单余额发生错误", error);
        }
    }

    /** 1.同步单个地址的电子面单账号 */
    /** 2.如果同步成功，重新获取电子面单列表 */
    async syncEwaybillAccountByAddressIdAndRefreshAccountList(ewaybillShippingAddressId: number) {
        try {
            this.loading = true;

            /** 同步【单个】电子面单账号的时候也抛出 502 触发前端引导授权弹窗 */
            await syncEwaybillAccount({ ewaybillShippingAddressId });

            this.loading = false;

            await this.loadEwaybillAccountData();
        } finally {
            this.loading = false;
        }
    }

    async loadEwaybillAccountData() {
        if (this.loading) {
            return;
        }

        try {
            this.loading = true;

            const {
                ewaybillShippingAddressMapping,
                branchAccountMapping,
                ewaybillAccountMapping,
                subscriptionMapping,
                rawEwaybillAccounts,
                branchAccountIdToBranchAccountMap,
                ewaybillAccountId2AccountMapping,
            } = await loadEwaybillAccountsList();

            this.rawEwaybillAccounts = observable(rawEwaybillAccounts, { deep: false });
            this.ewaybillAccountMapping = observable(ewaybillAccountMapping, { deep: false });
            this.ewaybillShippingAddressMapping = observable(ewaybillShippingAddressMapping, { deep: false });
            this.branchAccountMapping = observable(branchAccountMapping, { deep: false });
            this.subscriptionMapping = observable(subscriptionMapping, { deep: false });
            this.branchAccountIdToBranchAccountMap = observable(branchAccountIdToBranchAccountMap, { deep: false });
            this.ewaybillAccountId2AccountMapping = observable(ewaybillAccountId2AccountMapping, { deep: false });
        } finally {
            this.loading = false;
        }
    }

    clear() {
        this.rawEwaybillAccounts = [];
        this.ewaybillAccountMapping.clear();
        this.ewaybillShippingAddressMapping.clear();
        this.branchAccountMapping.clear();
        this.subscriptionMapping.clear();
        this.branchAccountIdToBranchAccountMap.clear();
    }
}

export default EwaybillAccountStore;
